(() => {
  if (customElements.get('abok-about-milestones')) return;

  class AbokAboutMilestones extends HTMLElement {
    connectedCallback() {
      this.disconnect();

      this.viewport = this.querySelector('[data-milestone-viewport]');
      this.cards = Array.from(this.querySelectorAll('[data-milestone-card]'));
      this.markers = Array.from(this.querySelectorAll('[data-milestone-marker]'));
      this.ruler = this.querySelector('[data-milestone-ruler]');
      this.handle = this.querySelector('[data-milestone-handle]');
      this.currentIndex = 0;
      this.controller = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.cards.length) return;

      this.style.setProperty('--abok-about-milestone-count', this.cards.length);
      this.style.setProperty('--abok-about-milestone-segments', Math.max(1, this.cards.length - 1));

      this.markers.forEach((marker) => {
        marker.addEventListener('click', () => this.activate(Number(marker.dataset.index)), {
          signal: this.controller.signal,
        });
        marker.addEventListener('keydown', (event) => this.onMarkerKeydown(event), {
          signal: this.controller.signal,
        });
      });

      if (this.handle) {
        this.handle.addEventListener('keydown', (event) => this.onHandleKeydown(event), {
          signal: this.controller.signal,
        });
        this.handle.addEventListener('pointerdown', (event) => this.onPointerDown(event), {
          signal: this.controller.signal,
        });
        this.handle.addEventListener('pointermove', (event) => this.onPointerMove(event), {
          signal: this.controller.signal,
        });
        this.handle.addEventListener('pointerup', (event) => this.onPointerUp(event), {
          signal: this.controller.signal,
        });
        this.handle.addEventListener('pointercancel', (event) => this.onPointerUp(event), {
          signal: this.controller.signal,
        });
      }

      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), {
        signal: this.controller.signal,
      });

      this.resizeObserver = new ResizeObserver(() => this.positionHandle());
      this.resizeObserver.observe(this);
      this.activate(0, { scroll: false });
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.isDragging = false;
    }

    activate(index, options = {}) {
      const nextIndex = Math.max(0, Math.min(this.cards.length - 1, index));
      const shouldScroll = options.scroll !== false;
      this.currentIndex = nextIndex;

      this.cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === nextIndex;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-current', isActive ? 'step' : 'false');
      });

      this.markers.forEach((marker, markerIndex) => {
        const isActive = markerIndex === nextIndex;
        marker.classList.toggle('is-active', isActive);
        marker.setAttribute('aria-current', isActive ? 'step' : 'false');
      });

      if (this.handle) {
        const card = this.cards[nextIndex];
        this.handle.setAttribute('aria-valuenow', String(nextIndex + 1));
        this.handle.setAttribute('aria-valuetext', card?.dataset.year || String(nextIndex + 1));
      }

      this.positionHandle();
      if (shouldScroll) this.scrollToCard(nextIndex);
    }

    positionHandle() {
      if (!this.ruler || !this.handle || this.cards.length < 2) return;
      const travel = Math.max(0, this.ruler.clientWidth - this.handle.offsetWidth);
      const ratio = this.currentIndex / (this.cards.length - 1);
      this.handle.style.transform = `translateX(${Math.round(travel * ratio)}px)`;
    }

    scrollToCard(index) {
      if (!this.viewport || !window.matchMedia('(min-width: 750px)').matches) return;
      const card = this.cards[index];
      const left = Math.max(0, card.offsetLeft - this.viewport.offsetLeft);
      this.viewport.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    onMarkerKeydown(event) {
      const current = Number(event.currentTarget.dataset.index);
      let nextIndex = null;

      if (event.key === 'ArrowLeft') nextIndex = current - 1;
      if (event.key === 'ArrowRight') nextIndex = current + 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.cards.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      nextIndex = Math.max(0, Math.min(this.cards.length - 1, nextIndex));
      this.markers[nextIndex]?.focus();
      this.activate(nextIndex);
    }

    onHandleKeydown(event) {
      let nextIndex = null;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') nextIndex = this.currentIndex - 1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') nextIndex = this.currentIndex + 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.cards.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      this.activate(nextIndex);
    }

    onPointerDown(event) {
      if (event.button !== 0 || !this.handle) return;
      this.isDragging = true;
      this.handle.setPointerCapture(event.pointerId);
      this.updateFromPointer(event);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      this.updateFromPointer(event);
    }

    onPointerUp(event) {
      if (!this.isDragging || !this.handle) return;
      this.isDragging = false;
      if (this.handle.hasPointerCapture(event.pointerId)) this.handle.releasePointerCapture(event.pointerId);
    }

    updateFromPointer(event) {
      if (!this.ruler || !this.handle || this.cards.length < 2) return;
      const bounds = this.ruler.getBoundingClientRect();
      const travel = Math.max(1, bounds.width - this.handle.offsetWidth);
      const localX = event.clientX - bounds.left - this.handle.offsetWidth / 2;
      const ratio = Math.max(0, Math.min(1, localX / travel));
      const index = Math.round(ratio * (this.cards.length - 1));
      if (index !== this.currentIndex) this.activate(index);
    }

    onBlockSelect(event) {
      const card = event.target?.closest?.('[data-milestone-card]');
      if (!card || !this.contains(card)) return;
      this.activate(Number(card.dataset.index));
    }
  }

  customElements.define('abok-about-milestones', AbokAboutMilestones);
})();
